import styled from 'styled-components';
import { Typography } from '@mui/material';
import {
  Alert,
  Chip,
  Flex,
  FlexColumn,
} from '@elementary/common-ui/components';

const CodeGray = styled.code`
  background-color: ${({ theme }) => theme.palette.common.gray1};
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 90%;
`;

const SectionHeading = styled(Typography)`
  font-weight: bold;
`;

const Highlight = styled.span`
  font-weight: bold;
`;

const SetupInstructionsWrapper = styled(FlexColumn)`
  gap: 16px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.palette.common.gray2};
  border-radius: 4px;
`;

type Props = {
  ctaLabel: string;
  atlasDomain?: string;
};

const JiraServiceAccountContent = ({ ctaLabel, atlasDomain }: Props) => (
  <FlexColumn sx={{ gap: 2 }}>
    <FlexColumn gap={1}>
      <Flex>
        <Chip label="Recommended" color="primary" size="medium" bold />
      </Flex>
      <Typography>
        Use a <Highlight>dedicated service account</Highlight> rather than a
        personal account. This keeps the integration{' '}
        <Highlight>stable long-term</Highlight> even if team members change
        roles or leave your organization.
      </Typography>
    </FlexColumn>

    <SetupInstructionsWrapper>
      <SectionHeading>How to set up a service account:</SectionHeading>
      <Typography>
        1. Create a new Atlassian account using a shared team email (e.g.{' '}
        <CodeGray>jira-bot@yourcompany.com</CodeGray>). Avoid using a personal
        email address.
      </Typography>
      <Typography>
        2. Add the account to your relevant Jira projects with permission to
        create and update issues.
      </Typography>
      <Typography>
        3. Click <Highlight>{ctaLabel}</Highlight> below and sign in with the
        service account when the popup appears.
      </Typography>
    </SetupInstructionsWrapper>

    <Alert severity="warning">
      <FlexColumn gap={1}>
        <Highlight>Heads up — watch which account you authorize</Highlight>
        <Typography sx={{ fontSize: '13px' }}>
          When the Atlassian authorization popup opens, make sure you are signed
          in as the <strong>service account</strong>, not your personal account.
          If your organization uses Google or Microsoft SSO, the popup will ask
          you to choose an account — select the service account there.{' '}
        </Typography>
        {atlasDomain && (
          <Typography sx={{ fontSize: '13px' }}>
            Make sure to connect to <CodeGray>{atlasDomain}</CodeGray> as it is
            the connected site.
          </Typography>
        )}
      </FlexColumn>
    </Alert>
  </FlexColumn>
);

export default JiraServiceAccountContent;
